/**
 * Two large photo panels side by side, each with a title, a chevron link and a description: the
 * child pages of a Metal Resources hub. Below `lg` the cards are drawn instead by
 * `src/components/Shared/EvenOddImageWithText/EvenOffImageWithText.tsx`.
 *
 * Used by `src/pages/metal-resources/references/index.tsx` (FAQ and Glossary) and
 * `src/pages/metal-resources/tools-calculators/index.tsx` (the two calculators, copy in
 * `src/content/ryerson/metal-resources/tools-calculators/index.ts` → `SubPages`).
 *
 * Rules a change must keep:
 * - Desktop reads `cards[0]` and `cards[1]` only: a third card shows on mobile alone, and fewer
 *   than two throws on desktop.
 * - The left panel's text padding follows the window width (the effect below): 140 px from 1440 px
 *   up; between 1200 and 1440 px, `(width - 1160) / 2`, the side margin of a centred 1160 px
 *   column; at 1200 px and below, 20 px left and 140 px right.
 */
import React from "react";
import { Icon } from "@ryerson/frontend.assets";
import { Media } from "@ryerson/frontend.next";
import { Typography } from "@ryerson/frontend.typography";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { Link } from "@ryerson/frontend.navigation";
import { css } from "@emotion/react";
import EvenOffImageWithText, {
	EvenOddImageWithTextCard,
} from "@components/Shared/EvenOddImageWithText/EvenOffImageWithText";

/** One child page's panel. */
export type SubPagesCard = {
	/** Panel heading; `\n` breaks the line (`white-space: pre-line`). */
	title: string;
	actionUrl: string;
	description: string;
	/** Site-relative background image path, served from `application/public/`. */
	imageUrl: string;
};

/** One language's panels, in display order (exactly two on desktop). */
export type SubPagesContent = {
	cards: SubPagesCard[];
};

/** Props for {@link SubPages}. */
export type SubPagesProps = {
	content: SubPagesContent;
};

const Contain = styled.div`
	width: 100%;
	height: 650px;
	display: block;
`;

const Left = styled.div`
	position: absolute;
	right: 49%;
	width: 51%;
	max-width: 740px;
	padding: 75px 0px 75px 95px;
	height: 510px;
	background-size: cover;
	background-position: center center;
`;

const Right = styled.div`
	position: absolute;
	left: 51%;
	width: 49%;
	max-width: 700px;
	height: 650px;
	background-size: cover;
	background-position: center center;
	@media (min-width: 1428px) {
		max-width: 710px;
	}
`;

const Overlay = styled.div`
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	padding: 75px 0 75px 95px;
	height: 100%;
	background: linear-gradient(6.93deg, rgba(0, 0, 0, 0.6) 10.83%, rgba(0, 0, 0, 0.132) 79.88%);
`;

/** Renders the two child-page panels for one language. */
const SubPages: React.FunctionComponent<SubPagesProps> = ({ content }) => {
	const { theme } = useTheme();

	const cardArray: EvenOddImageWithTextCard[] = [];
	content.cards.forEach((card) => {
		cardArray.push({
			title: card.title,
			description: card.description,
			imageUrl: card.imageUrl,
			actionUrl: card.actionUrl,
		});
	});

	const [paddingLeft, setPaddingLeft] = React.useState<number>(140);
	const [paddingRight, setPaddingRight] = React.useState<number>(0);

	// Sets the padding once on mount and again on every resize. The listener is never removed,
	// so it outlives the component.
	React.useEffect(() => {
		if (window) {
			window.addEventListener("resize", () => {
				let width = window.innerWidth;
				if (width >= 1440) {
					setPaddingLeft(140);
					setPaddingRight(0);
				} else if (width <= 1200) {
					setPaddingLeft(20);
					setPaddingRight(140);
				} else if (width > 1200 && width < 1440) {
					setPaddingLeft((width - 1160) / 2);
					setPaddingRight(0);
				}
			});
			let width = window.innerWidth;
			if (width >= 1440) {
				setPaddingLeft(140);
				setPaddingRight(0);
			} else if (width <= 1200) {
				setPaddingLeft(20);
				setPaddingRight(140);
			} else if (width > 1200 && width < 1440) {
				setPaddingLeft((width - 1160) / 2);
				setPaddingRight(0);
			}
		}
	}, []);

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection type="secondary" vPadding="0px">
					<Contain>
						<Left
							css={css`
								background-image: url(${content.cards[0].imageUrl});
							`}
						>
							<Overlay
								css={css`
									padding-left: ${paddingLeft + "px"} !important;
									padding-right: ${paddingRight + "px"} !important;
								`}
							>
								<Flex
									css={css`
										height: 100%;
									`}
									direction="column"
									justifyContent="flex-end"
								>
									<FlexItem>
										<Typography
											type="tertiary"
											css={css`
												white-space: pre-line;
												margin-bottom: 53px;
											`}
											variant="h1"
										>
											{content.cards[0].title}
											<Link to={content.cards[0].actionUrl}>
												<Icon
													icon="chevron-right"
													circled={true}
													css={css`
														margin-left: 25px;
														display: inline-flex;
														line-height: 56px;
														vertical-align: middle;
														cursor: pointer;
													`}
													color={theme.colors.primary.white}
													size="xl"
												/>
											</Link>
										</Typography>
										<Typography
											css={css`
												display: block;
												max-width: 360px;
												margin-top: 30px;
											`}
											size="md"
											color={theme.colors.primary.white}
										>
											{content.cards[0].description}
										</Typography>
									</FlexItem>
								</Flex>
							</Overlay>
						</Left>
						<Right
							css={css`
								background-image: url(${content.cards[1].imageUrl});
							`}
						>
							<Overlay>
								<Flex
									css={css`
										height: 100%;
									`}
									direction="column"
									justifyContent="flex-end"
								>
									<FlexItem>
										<Typography
											type="tertiary"
											css={css`
												white-space: pre-line;
												margin-bottom: 53px;
											`}
											variant="h1"
										>
											{content.cards[1].title}
											<Link to={content.cards[1].actionUrl}>
												<Icon
													icon="chevron-right"
													circled={true}
													css={css`
														margin-left: 25px;
														display: inline-flex;
														line-height: 56px;
														cursor: pointer;
														vertical-align: middle;
													`}
													color={theme.colors.primary.white}
													size="xl"
												/>
											</Link>
										</Typography>
										<Typography
											css={css`
												display: block;
												max-width: 360px;
												margin-top: 30px;
											`}
											size="md"
											color={theme.colors.primary.white}
										>
											{content.cards[1].description}
										</Typography>
									</FlexItem>
								</Flex>
							</Overlay>
						</Right>
					</Contain>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="primary" vPadding="20px">
					<EvenOffImageWithText cards={cardArray} />
				</ContentSection>
			</Media>
		</>
	);
};

export default SubPages;
